import { addScanFromScanner, sendMessage } from "actions";
import {
  DriverEventType,
  type DriverEffect,
  type DriverNotifier,
} from "lib/deviceDrivers/sessionFramework/adapter/driverEffects";
import {
  Xrp2iErrorEvent,
  Xrp2iEvent,
  Xrp2iEventType,
  Xrp2iScanEvent,
  Xrp2iTimeSyncedEvent,
} from "lib/deviceDrivers/truTest/xrp2i/device/types";

export type { DriverEffect, DriverNotifier };

export type DriverAction =
  | ReturnType<typeof addScanFromScanner>
  | ReturnType<typeof sendMessage>;

export type DriverActionResult = DriverAction[];

/**
 * Maps a raw device event into the Redux actions or toast notifications the rest of the app expects. Keeping this translation here means the session framework does not need to know anything about the specific events this driver produces.
 */
export function mapDeviceEventToDriverEffects(
  deviceId: string,
  event: Xrp2iEvent,
): DriverEffect<DriverAction>[] {
  switch (event.type) {
    case Xrp2iEventType.SCAN:
      return mapScanEventToEffects(deviceId, event);

    case Xrp2iEventType.ERROR:
      return mapErrorEventToEffects(event);

    case Xrp2iEventType.INVALID_EID:
      return mapInvalidEidEventToEffects();

    case Xrp2iEventType.TIME_SYNCED:
      return mapTimeSyncedEventToEffects(event);

    default:
      return assertNever(event);
  }
}

/**
 * Throws if the switch reaches an event type with no handler, catching missing cases at runtime. TypeScript flags the same gap at compile time, but the runtime error is a useful safety net when running compiled tests.
 */
function assertNever(value: never): never {
  throw new Error(`Unhandled XRP2i event: ${JSON.stringify(value)}`);
}

/**
 * Converts a successful scan event into an addScanFromScanner Redux action for the given device. The action carries both the EID and timestamp so the store can record the scan without the caller needing to know which scanner produced it.
 */
function mapScanEventToEffects(
  deviceId: string,
  event: Xrp2iScanEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.ACTION,
      action: addScanFromScanner(deviceId, {
        created: event.created,
        EID: event.eid,
        draftName: "",
      }),
    },
  ];
}

/**
 * Converts a device error event into a toast notification showing the scanner's error code. The code is shown as-is because the error set comes from the scanner firmware and the app cannot do anything beyond alerting the operator.
 */
function mapErrorEventToEffects(
  event: Xrp2iErrorEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.INTERNAL_ERROR,
      internalError: `Error communicating with XRS2i (${event.code})`,
    },
  ];
}

/**
 * Converts an invalid-EID signal into a toast notification so the operator knows the scanner read something it could not decode. No Redux action is raised because there is no scan data worth storing.
 */
function mapInvalidEidEventToEffects(): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.NOTIFY,
      message: "Received invalid EID data from scanner",
    },
  ];
}

const SIGNIFICANT_DRIFT_MS = 5 * 60 * 1000;

/**
 * Warns the operator when the scanner clock was measurably out of sync before correction. Drift under one minute is treated as normal RTC variance and suppressed.
 */
function mapTimeSyncedEventToEffects(
  event: Xrp2iTimeSyncedEvent,
): DriverEffect<DriverAction>[] {
  if (Math.abs(event.driftMs) < SIGNIFICANT_DRIFT_MS) {
    return [];
  }
  return [
    {
      type: DriverEventType.INFO,
      message: `Scanner time updated - ${formatDrift(event.driftMs)} out`,
    },
  ];
}

function formatDrift(driftMs: number): string {
  const totalSeconds = Math.round(Math.abs(driftMs) / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds === 1 ? "" : "s"}`;
  }
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.round(hours / 24);
  return `${days} days`;
}
